import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api } from '../api/client';
import { gravarSessao, limparSessao, lerSessao } from '../api/tokenStore';
import type { RespostaDeLogin, Sessao, Vinculo, Vocabulario } from '../api/types';

/**
 * Quem está logado, em que empresa, com quais papéis — e o dicionário.
 *
 * <h3>Os papéis vêm do servidor e servem só para ESCONDER</h3>
 * Um botão escondido não é autorização: a autorização é o `@PreAuthorize` no serviço, e ela
 * continua valendo com o painel inteiro reescrito por quem quiser. O que o painel faz aqui é
 * não oferecer o que vai ser recusado — o que é cortesia, não segurança. Tratá-lo como
 * segurança seria o erro clássico desta camada.
 *
 * <h3>O dicionário carrega junto</h3>
 * Nenhuma tela renderiza enum cru. Se o dicionário não chegar, é melhor a casca não abrir do
 * que abrir mostrando `SUSPECTED_CLONE` para o cliente.
 */

interface Autenticacao {
  sessao: Sessao | null;
  vocabulario: Vocabulario;
  carregando: boolean;
  entrar: (email: string, senha: string, membershipId?: string) => Promise<Vinculo[] | null>;
  sair: () => void;
  temPapel: (...papeis: string[]) => boolean;
  traduzir: (familia: string, valor: string | null | undefined) => string;
}

const Contexto = createContext<Autenticacao | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [vocabulario, setVocabulario] = useState<Vocabulario>({});
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    if (!lerSessao()) {
      setSessao(null);
      setCarregando(false);
      return;
    }
    try {
      const [eu, dicionario] = await Promise.all([
        api.get<Sessao>('/console/me'),
        api.get<Vocabulario>('/console/vocabulary'),
      ]);
      setSessao(eu);
      setVocabulario(dicionario);
    } catch {
      limparSessao();
      setSessao(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const entrar = useCallback(
    async (email: string, senha: string, membershipId?: string) => {
      const r = await api.post<RespostaDeLogin>('/auth/login', {
        email,
        password: senha,
        membershipId: membershipId ?? null,
      });

      // A pessoa atende mais de uma empresa e ainda não disse por qual está entrando. Não há
      // token nenhum nesta resposta — o contexto de banco tem UMA empresa, uma só.
      if (r.nextStep === 'SELECT_MEMBERSHIP' || !r.accessToken || !r.refreshToken) {
        return r.memberships;
      }

      gravarSessao({
        accessToken: r.accessToken,
        refreshToken: r.refreshToken,
        userId: r.userId,
        nome: r.fullName,
      });
      setCarregando(true);
      await carregar();
      return null;
    },
    [carregar],
  );

  const sair = useCallback(() => {
    limparSessao();
    setSessao(null);
  }, []);

  const valor = useMemo<Autenticacao>(
    () => ({
      sessao,
      vocabulario,
      carregando,
      entrar,
      sair,
      temPapel: (...papeis: string[]) =>
        !!sessao && papeis.some((p) => sessao.papeis.includes(p)),
      traduzir: (familia, valor2) => {
        if (!valor2) {
          return '—';
        }
        // Sem tradução, mostra o valor cru E deixa isso visível no console. O servidor
        // quebra o build quando falta chave (VocabularioTest); se algo escapou até aqui,
        // esconder o buraco atrasaria a descoberta.
        const texto = vocabulario[familia]?.[valor2];
        if (!texto) {
          console.warn(`sem tradução para ${familia}.${valor2}`);
          return valor2;
        }
        return texto;
      },
    }),
    [sessao, vocabulario, carregando, entrar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): Autenticacao {
  const c = useContext(Contexto);
  if (!c) {
    throw new Error('useAuth fora do AuthProvider');
  }
  return c;
}
